import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';

const API_URL = process.env.REACT_APP_API_URL || '';

const styles = {
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '15px',
    marginBottom: '25px'
  },
  card: {
    background: 'white',
    borderRadius: '12px',
    padding: '20px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.05)'
  },
  statCard: {
    background: 'linear-gradient(135deg, #722F37 0%, #8B4049 100%)',
    color: 'white'
  },
  statValue: {
    fontSize: '32px',
    fontWeight: '700',
    marginBottom: '5px'
  },
  statLabel: {
    fontSize: '13px',
    opacity: '0.9'
  },
  cardTitle: {
    fontSize: '16px',
    fontWeight: '600',
    marginBottom: '15px',
    color: '#333',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#722F37',
    marginBottom: '15px',
    marginTop: '25px',
    paddingBottom: '10px',
    borderBottom: '2px solid #722F37'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '13px'
  },
  th: {
    textAlign: 'left',
    padding: '10px 8px',
    borderBottom: '2px solid #f0f0f0',
    fontSize: '12px',
    fontWeight: '600',
    color: '#666',
    whiteSpace: 'nowrap'
  },
  td: {
    padding: '10px 8px',
    borderBottom: '1px solid #f0f0f0',
    fontSize: '13px'
  },
  progressBar: {
    height: '8px',
    background: '#e9ecef',
    borderRadius: '4px',
    overflow: 'hidden',
    minWidth: '80px'
  },
  progressFill: {
    height: '100%',
    borderRadius: '4px',
    transition: 'width 0.3s ease'
  },
  badge: {
    display: 'inline-block',
    padding: '3px 8px',
    borderRadius: '12px',
    fontSize: '11px',
    fontWeight: '600'
  },
  badgeUp: {
    background: '#d4edda',
    color: '#155724'
  },
  badgeDown: {
    background: '#f8d7da',
    color: '#721c24'
  },
  badgeNeutral: {
    background: '#e2e3e5',
    color: '#383d41'
  },
  emptyState: {
    textAlign: 'center',
    padding: '40px',
    color: '#888'
  },
  tabs: {
    display: 'flex',
    gap: '10px',
    marginBottom: '20px',
    borderBottom: '2px solid #eee',
    paddingBottom: '10px',
    flexWrap: 'wrap'
  },
  tab: {
    padding: '8px 16px',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    color: '#666',
    borderRadius: '6px',
    transition: 'all 0.2s'
  },
  tabActive: {
    background: '#722F37',
    color: 'white'
  },
  kpiRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 0',
    borderBottom: '1px solid #f0f0f0'
  },
  kpiLabel: {
    flex: 1,
    fontSize: '13px',
    color: '#333'
  },
  kpiValue: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#722F37',
    marginLeft: '10px'
  },
  yearSelector: {
    display: 'flex',
    gap: '8px',
    marginBottom: '20px',
    flexWrap: 'wrap',
    alignItems: 'center'
  },
  yearBtn: {
    padding: '8px 16px',
    border: '2px solid #722F37',
    background: 'white',
    color: '#722F37',
    borderRadius: '20px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '500',
    transition: 'all 0.2s'
  },
  yearBtnActive: {
    background: '#722F37',
    color: 'white'
  },
  comparisonHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    flexWrap: 'wrap',
    gap: '15px'
  },
  select: {
    padding: '8px 12px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '14px',
    outline: 'none',
    cursor: 'pointer'
  },
  comparisonCard: {
    background: 'linear-gradient(135deg, #f8f9fa 0%, #fff 100%)',
    border: '2px solid #e0e0e0'
  },
  trendIndicator: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '12px',
    fontWeight: '600'
  },
  trendUp: {
    color: '#28a745'
  },
  trendDown: {
    color: '#dc3545'
  }
};

const getColorByValue = (value) => {
  if (value >= 85) return '#28a745';
  if (value >= 75) return '#ffc107';
  if (value >= 65) return '#fd7e14';
  return '#dc3545';
};

const availableYears = ['2024', '2023'];

function AdminDashboard() {
  const [data, setData] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('resumen');
  const [selectedYear, setSelectedYear] = useState('2024');
  const [compareYear, setCompareYear] = useState('2023');
  const [showComparison, setShowComparison] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('adminToken');
        if (!token) {
          window.location.href = '/admin';
          return;
        }

        const [dashRes, kpiRes] = await Promise.all([
          fetch(`${API_URL}/api/admin/dashboard`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          fetch(`${API_URL}/api/admin/kpis`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);

        if (dashRes.status === 401 || kpiRes.status === 401) {
          localStorage.removeItem('adminToken');
          window.location.href = '/admin';
          return;
        }

        const [dashData, kpiData] = await Promise.all([
          dashRes.json(),
          kpiRes.json()
        ]);

        setData(dashData);
        setKpis(kpiData);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <AdminLayout title="Dashboard">
        <div style={styles.emptyState}>Cargando...</div>
      </AdminLayout>
    );
  }

  const campaign = data?.campaign;
  const stats = data?.stats || { completed: 0, pending: 0, total: 0, participationRate: 0 };

  // Calculate trend
  const getTrend = (current, previous) => {
    if (!previous || previous === 0) return null;
    const diff = current - previous;
    return {
      value: diff.toFixed(1),
      direction: diff > 0 ? 'up' : diff < 0 ? 'down' : 'neutral'
    };
  };

  return (
    <AdminLayout title="Dashboard - Encuesta de Clima Laboral">
      {/* Year selector */}
      {kpis?.hasData && (
        <div style={styles.comparisonHeader}>
          <div style={styles.yearSelector}>
            <span style={{ fontSize: '14px', color: '#666', marginRight: '10px' }}>Año:</span>
            {availableYears.map(year => (
              <button
                key={year}
                style={{
                  ...styles.yearBtn,
                  ...(selectedYear === year ? styles.yearBtnActive : {})
                }}
                onClick={() => setSelectedYear(year)}
              >
                {year}
              </button>
            ))}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <label style={{ fontSize: '14px', color: '#666' }}>
              <input
                type="checkbox"
                checked={showComparison}
                onChange={(e) => setShowComparison(e.target.checked)}
                style={{ marginRight: '6px' }}
              />
              Comparar con:
            </label>
            <select
              style={styles.select}
              value={compareYear}
              onChange={(e) => setCompareYear(e.target.value)}
              disabled={!showComparison}
            >
              {availableYears.filter(y => y !== selectedYear).map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={styles.tabs}>
        <button
          style={{ ...styles.tab, ...(activeTab === 'resumen' ? styles.tabActive : {}) }}
          onClick={() => setActiveTab('resumen')}
        >
          Resumen General
        </button>
        <button
          style={{ ...styles.tab, ...(activeTab === 'dimensiones' ? styles.tabActive : {}) }}
          onClick={() => setActiveTab('dimensiones')}
        >
          Por Dimensiones
        </button>
        <button
          style={{ ...styles.tab, ...(activeTab === 'departamentos' ? styles.tabActive : {}) }}
          onClick={() => setActiveTab('departamentos')}
        >
          Por Departamentos
        </button>
        <button
          style={{ ...styles.tab, ...(activeTab === 'comparativa' ? styles.tabActive : {}) }}
          onClick={() => setActiveTab('comparativa')}
        >
          Comparativa Anual
        </button>
        <button
          style={{ ...styles.tab, ...(activeTab === 'actual' ? styles.tabActive : {}) }}
          onClick={() => setActiveTab('actual')}
        >
          Campaña Actual
        </button>
      </div>

      {/* RESUMEN TAB */}
      {activeTab === 'resumen' && kpis?.hasData && (
        <>
          {/* Main KPIs */}
          <div style={styles.grid}>
            <div style={{ ...styles.card, ...styles.statCard }}>
              <div style={styles.statValue}>{kpis.resumen.total_respuestas}</div>
              <div style={styles.statLabel}>Total Respuestas {selectedYear}</div>
            </div>
            <div style={styles.card}>
              <div style={{ ...styles.statValue, color: '#722F37' }}>{kpis.resumen.participacion_total}%</div>
              <div style={{ ...styles.statLabel, color: '#666' }}>Participación Global</div>
            </div>
            <div style={styles.card}>
              <div style={{ ...styles.statValue, color: getColorByValue(kpis.resumen.satisfaccion_colaboradores) }}>
                {kpis.resumen.satisfaccion_colaboradores.toFixed(1)}%
              </div>
              <div style={{ ...styles.statLabel, color: '#666' }}>Satisfacción Colaboradores</div>
            </div>
            <div style={styles.card}>
              <div style={{ ...styles.statValue, color: getColorByValue(kpis.resumen.satisfaccion_direccion) }}>
                {kpis.resumen.satisfaccion_direccion.toFixed(1)}%
              </div>
              <div style={{ ...styles.statLabel, color: '#666' }}>Satisfacción Dirección</div>
            </div>
          </div>

          {/* Participation breakdown */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '25px' }}>
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>Participación por Tipo</h3>
              <div style={styles.kpiRow}>
                <span style={styles.kpiLabel}>Colaboradores</span>
                <span style={styles.kpiValue}>{kpis.resumen.respuestas_colaboradores} respuestas</span>
              </div>
              <div style={styles.kpiRow}>
                <span style={styles.kpiLabel}>Comité de Dirección</span>
                <span style={styles.kpiValue}>{kpis.resumen.respuestas_direccion} respuestas</span>
              </div>
            </div>

            <div style={{ ...styles.card, ...styles.comparisonCard }}>
              <h3 style={styles.cardTitle}>
                Evolución Dirección
                {showComparison && <span style={{ fontSize: '12px', color: '#666' }}>{selectedYear} vs {compareYear}</span>}
              </h3>
              <div style={styles.kpiRow}>
                <span style={styles.kpiLabel}>Satisfacción {selectedYear}</span>
                <span style={styles.kpiValue}>{kpis.resumen.satisfaccion_direccion.toFixed(1)}%</span>
              </div>
              {showComparison && (
                <>
                  <div style={styles.kpiRow}>
                    <span style={styles.kpiLabel}>Satisfacción {compareYear}</span>
                    <span style={styles.kpiValue}>{kpis.resumen.satisfaccion_direccion_2023.toFixed(1)}%</span>
                  </div>
                  <div style={{ ...styles.kpiRow, borderBottom: 'none' }}>
                    <span style={styles.kpiLabel}>Variación</span>
                    {(() => {
                      const trend = getTrend(kpis.resumen.satisfaccion_direccion, kpis.resumen.satisfaccion_direccion_2023);
                      return trend && (
                        <span style={{
                          ...styles.trendIndicator,
                          ...(trend.direction === 'up' ? styles.trendUp : trend.direction === 'down' ? styles.trendDown : {})
                        }}>
                          {trend.direction === 'up' ? '↑' : trend.direction === 'down' ? '↓' : '→'}
                          {trend.direction === 'up' ? '+' : ''}{trend.value}%
                        </span>
                      );
                    })()}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Best and worst dimensions */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <div style={styles.card}>
              <h3 style={{ ...styles.cardTitle, color: '#28a745' }}>
                ✓ Mejores Dimensiones
              </h3>
              {kpis.mejores_dimensiones.map((dim, i) => (
                <div key={dim.id} style={styles.kpiRow}>
                  <span style={styles.kpiLabel}>{i + 1}. {dim.nombre}</span>
                  <span style={{ ...styles.kpiValue, color: '#28a745' }}>
                    {dim.colaboradores_2024_pct.toFixed(1)}%
                  </span>
                </div>
              ))}
            </div>

            <div style={styles.card}>
              <h3 style={{ ...styles.cardTitle, color: '#dc3545' }}>
                ⚠ Áreas de Mejora
              </h3>
              {kpis.peores_dimensiones.map((dim, i) => (
                <div key={dim.id} style={styles.kpiRow}>
                  <span style={styles.kpiLabel}>{i + 1}. {dim.nombre}</span>
                  <span style={{ ...styles.kpiValue, color: '#dc3545' }}>
                    {dim.colaboradores_2024_pct.toFixed(1)}%
                  </span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* DIMENSIONES TAB */}
      {activeTab === 'dimensiones' && kpis?.hasData && (
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>Satisfacción por Dimensión - Encuesta {selectedYear}</h3>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Dimensión</th>
                <th style={{ ...styles.th, textAlign: 'center' }}>Colaboradores</th>
                <th style={{ ...styles.th, textAlign: 'center' }}>Dirección {selectedYear}</th>
                {showComparison && (
                  <>
                    <th style={{ ...styles.th, textAlign: 'center' }}>Dirección {compareYear}</th>
                    <th style={{ ...styles.th, textAlign: 'center' }}>Variación</th>
                  </>
                )}
                <th style={{ ...styles.th, width: '150px' }}>Progreso</th>
              </tr>
            </thead>
            <tbody>
              {kpis.dimensiones
                .sort((a, b) => a.orden - b.orden)
                .map(dim => {
                  const trend = showComparison ? getTrend(dim.direccion_2024_pct, dim.direccion_2023_pct) : null;
                  return (
                    <tr key={dim.id}>
                      <td style={styles.td}>{dim.nombre}</td>
                      <td style={{ ...styles.td, textAlign: 'center', fontWeight: '600' }}>
                        {dim.colaboradores_2024_pct.toFixed(1)}%
                      </td>
                      <td style={{ ...styles.td, textAlign: 'center' }}>
                        {dim.direccion_2024_pct.toFixed(1)}%
                      </td>
                      {showComparison && (
                        <>
                          <td style={{ ...styles.td, textAlign: 'center', color: '#666' }}>
                            {dim.direccion_2023_pct.toFixed(1)}%
                          </td>
                          <td style={{ ...styles.td, textAlign: 'center' }}>
                            {trend && (
                              <span style={{
                                ...styles.badge,
                                ...(trend.direction === 'up' ? styles.badgeUp : trend.direction === 'down' ? styles.badgeDown : styles.badgeNeutral)
                              }}>
                                {trend.direction === 'up' ? '+' : ''}{trend.value}%
                              </span>
                            )}
                          </td>
                        </>
                      )}
                      <td style={styles.td}>
                        <div style={styles.progressBar}>
                          <div style={{
                            ...styles.progressFill,
                            width: `${dim.colaboradores_2024_pct}%`,
                            background: getColorByValue(dim.colaboradores_2024_pct)
                          }} />
                        </div>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      )}

      {/* DEPARTAMENTOS TAB */}
      {activeTab === 'departamentos' && kpis?.hasData && (
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>Satisfacción por Departamento - Encuesta {selectedYear}</h3>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Departamento</th>
                <th style={{ ...styles.th, textAlign: 'center' }}>Satisfacción Total</th>
                <th style={{ ...styles.th, width: '200px' }}>Progreso</th>
              </tr>
            </thead>
            <tbody>
              {kpis.por_departamento
                .sort((a, b) => b.satisfaccion_total - a.satisfaccion_total)
                .map((dept, i) => (
                  <tr key={i}>
                    <td style={styles.td}>{dept.departamento_original}</td>
                    <td style={{ ...styles.td, textAlign: 'center', fontWeight: '600' }}>
                      {(dept.satisfaccion_total * 20).toFixed(1)}%
                    </td>
                    <td style={styles.td}>
                      <div style={styles.progressBar}>
                        <div style={{
                          ...styles.progressFill,
                          width: `${dept.satisfaccion_total * 20}%`,
                          background: getColorByValue(dept.satisfaccion_total * 20)
                        }} />
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      {/* COMPARATIVA TAB */}
      {activeTab === 'comparativa' && kpis?.hasData && (
        <>
          <div style={{ ...styles.card, marginBottom: '20px' }}>
            <h3 style={styles.cardTitle}>Comparativa {selectedYear} vs {compareYear} - Dirección</h3>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Dimensión</th>
                  <th style={{ ...styles.th, textAlign: 'center', background: '#722F37', color: 'white' }}>{selectedYear}</th>
                  <th style={{ ...styles.th, textAlign: 'center', background: '#8B4049', color: 'white' }}>{compareYear}</th>
                  <th style={{ ...styles.th, textAlign: 'center' }}>Diferencia</th>
                  <th style={{ ...styles.th, textAlign: 'center' }}>Tendencia</th>
                </tr>
              </thead>
              <tbody>
                {kpis.dimensiones
                  .sort((a, b) => a.orden - b.orden)
                  .map(dim => {
                    const diff = dim.direccion_2024_pct - dim.direccion_2023_pct;
                    return (
                      <tr key={dim.id}>
                        <td style={styles.td}>{dim.nombre}</td>
                        <td style={{ ...styles.td, textAlign: 'center', fontWeight: '600' }}>
                          {dim.direccion_2024_pct.toFixed(1)}%
                        </td>
                        <td style={{ ...styles.td, textAlign: 'center' }}>
                          {dim.direccion_2023_pct.toFixed(1)}%
                        </td>
                        <td style={{ ...styles.td, textAlign: 'center', fontWeight: '600', color: diff > 0 ? '#28a745' : diff < 0 ? '#dc3545' : '#666' }}>
                          {diff > 0 ? '+' : ''}{diff.toFixed(1)}%
                        </td>
                        <td style={{ ...styles.td, textAlign: 'center' }}>
                          <span style={{
                            ...styles.badge,
                            ...(diff > 0 ? styles.badgeUp : diff < 0 ? styles.badgeDown : styles.badgeNeutral)
                          }}>
                            {diff > 0 ? '↑ Mejora' : diff < 0 ? '↓ Baja' : '→ Igual'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <div style={{ ...styles.card, background: '#d4edda' }}>
              <h3 style={{ ...styles.cardTitle, color: '#155724' }}>
                ↑ Dimensiones que Mejoran
              </h3>
              {kpis.dimensiones
                .filter(dim => dim.direccion_2024_pct > dim.direccion_2023_pct)
                .sort((a, b) => (b.direccion_2024_pct - b.direccion_2023_pct) - (a.direccion_2024_pct - a.direccion_2023_pct))
                .slice(0, 5)
                .map((dim, i) => (
                  <div key={dim.id} style={{ ...styles.kpiRow, borderColor: '#c3e6cb' }}>
                    <span style={styles.kpiLabel}>{dim.nombre}</span>
                    <span style={{ ...styles.kpiValue, color: '#155724' }}>
                      +{(dim.direccion_2024_pct - dim.direccion_2023_pct).toFixed(1)}%
                    </span>
                  </div>
                ))}
              {kpis.dimensiones.filter(dim => dim.direccion_2024_pct > dim.direccion_2023_pct).length === 0 && (
                <p style={{ color: '#666', textAlign: 'center', padding: '20px' }}>No hay mejoras</p>
              )}
            </div>

            <div style={{ ...styles.card, background: '#f8d7da' }}>
              <h3 style={{ ...styles.cardTitle, color: '#721c24' }}>
                ↓ Dimensiones que Bajan
              </h3>
              {kpis.dimensiones
                .filter(dim => dim.direccion_2024_pct < dim.direccion_2023_pct)
                .sort((a, b) => (a.direccion_2024_pct - a.direccion_2023_pct) - (b.direccion_2024_pct - b.direccion_2023_pct))
                .slice(0, 5)
                .map((dim, i) => (
                  <div key={dim.id} style={{ ...styles.kpiRow, borderColor: '#f5c6cb' }}>
                    <span style={styles.kpiLabel}>{dim.nombre}</span>
                    <span style={{ ...styles.kpiValue, color: '#721c24' }}>
                      {(dim.direccion_2024_pct - dim.direccion_2023_pct).toFixed(1)}%
                    </span>
                  </div>
                ))}
              {kpis.dimensiones.filter(dim => dim.direccion_2024_pct < dim.direccion_2023_pct).length === 0 && (
                <p style={{ color: '#666', textAlign: 'center', padding: '20px' }}>No hay bajadas</p>
              )}
            </div>
          </div>
        </>
      )}

      {/* CAMPAÑA ACTUAL TAB */}
      {activeTab === 'actual' && (
        <>
          {campaign ? (
            <>
              <div style={{
                background: '#f8f9fa',
                borderRadius: '8px',
                padding: '15px',
                marginBottom: '20px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: '16px', fontWeight: '600', color: '#333' }}>{campaign.nombre}</div>
                    <div style={{ fontSize: '13px', color: '#666', marginTop: '5px' }}>
                      {campaign.fecha_inicio} - {campaign.fecha_fin}
                    </div>
                  </div>
                  <span style={{ ...styles.badge, background: '#d4edda', color: '#155724', padding: '5px 12px' }}>
                    Activa
                  </span>
                </div>
              </div>

              <div style={styles.grid}>
                <div style={{ ...styles.card, ...styles.statCard }}>
                  <div style={styles.statValue}>{stats.completed || 0}</div>
                  <div style={styles.statLabel}>Encuestas Completadas</div>
                </div>
                <div style={styles.card}>
                  <div style={{ ...styles.statValue, color: '#722F37' }}>{stats.pending || 0}</div>
                  <div style={{ ...styles.statLabel, color: '#666' }}>Pendientes</div>
                </div>
                <div style={styles.card}>
                  <div style={{ ...styles.statValue, color: '#722F37' }}>{stats.total || 0}</div>
                  <div style={{ ...styles.statLabel, color: '#666' }}>Total Enviadas</div>
                </div>
                <div style={styles.card}>
                  <div style={{ ...styles.statValue, color: '#28a745' }}>{stats.participationRate || 0}%</div>
                  <div style={{ ...styles.statLabel, color: '#666' }}>Tasa de Participación</div>
                </div>
              </div>
            </>
          ) : (
            <div style={styles.emptyState}>
              <p>No hay campaña activa en este momento.</p>
              <p style={{ fontSize: '13px', color: '#999', marginTop: '10px' }}>
                Ve a Configuración para crear o activar una campaña.
              </p>
            </div>
          )}
        </>
      )}

      {/* No KPI data message */}
      {!kpis?.hasData && activeTab !== 'actual' && (
        <div style={styles.emptyState}>
          <p>No hay datos históricos cargados.</p>
        </div>
      )}
    </AdminLayout>
  );
}

export default AdminDashboard;
